<template>
  <div class="auto-include-rules-editor">

    <EmptyState
      v-if="!localRules.length"
      title="Nog geen auto-include items"
      description="Voeg items toe die automatisch meegeleverd worden bij dit product"
    >
      <Btn variant="secondary" @click="addRule" icon="plus">Eerste Item Toevoegen</Btn>
    </EmptyState>

    <div v-else class="rules-list">
      <div v-for="(rule, idx) in localRules" :key="rule.id" class="rule-card">
        <div class="rule-header">
          <div class="rule-info">
            <!-- Source type selector -->
            <SelectInput
              :id="`rule-source-${idx}`"
              label="Bron"
              v-model="rule.sourceType"
              :options="sourceTypeOptions"
              class="rule-source-select"
            />

            <!-- Product search (when source = product) -->
            <template v-if="rule.sourceType === 'product'">
              <SearchableProductSelect
                :id="`rule-product-${idx}`"
                label="Product"
                v-model="rule.name"
                placeholder="Zoek een product..."
                @productSelected="(product) => handleProductSelected(idx, product)"
                class="rule-product-select"
              />
              <div class="product-info-display" v-if="rule.productId">
                <span class="product-sku-badge">{{ rule.sku || 'Geen SKU' }}</span>
                <span class="product-price-badge">{{ formatCurrency(rule.price) }}</span>
              </div>
            </template>

            <!-- Manual input (when source = manual) -->
            <template v-else>
              <TextInput
                :id="`rule-name-${idx}`"
                label="Naam"
                v-model="rule.name"
                placeholder="bijv. Montage, Haringen set"
                class="rule-name-input"
              />
              <TextInput
                :id="`rule-sku-${idx}`"
                label="SKU"
                v-model="rule.sku"
                placeholder="bijv. MONTAGE-001"
                class="rule-sku-input"
              />
              <CurrencyInput
                :id="`rule-price-${idx}`"
                label="Prijs per stuk"
                v-model="rule.price"
                class="rule-price-input"
              />
            </template>
          </div>
          <Btn
            icon="trash"
            variant="plain"
            size="sm"
            @click="removeRule(idx)"
            title="Verwijder item"
          />
        </div>

        <div class="rule-body">
          <!-- Quantity Section -->
          <div class="rule-section">
            <h5>Hoeveelheid</h5>
            <div class="quantity-row">
              <SelectInput
                :id="`quantity-type-${idx}`"
                label="Type"
                v-model="rule.quantityType"
                :options="quantityTypeOptions"
                class="quantity-type-select"
              />

              <NumberInput
                v-if="rule.quantityType === 'fixed'"
                :id="`quantity-value-${idx}`"
                label="Aantal"
                v-model="rule.quantity"
                :min="1"
                class="quantity-value-input"
              />

              <div v-else class="formula-input-group">
                <FormulaEditor
                  :id="`quantity-formula-${idx}`"
                  label="Formule voor aantal"
                  v-model="rule.quantityFormula"
                  :custom-fields="formulaFields"
                  :show-conditional-operators="false"
                  compact
                />
                <p class="formula-help">
                  <small>Tip: Gebruik <code>Math.ceil()</code> voor afronden naar boven, <code>Math.floor()</code> voor naar beneden</small>
                </p>
              </div>
            </div>
          </div>

          <!-- Visibility Section -->
          <div class="rule-section">
            <h5>Zichtbaarheid</h5>
            <div class="visibility-row">
              <ToggleInput
                :id="`show-quote-${idx}`"
                label="Toon op offerte"
                v-model="rule.showOnQuote"
              />
              <ToggleInput
                :id="`show-packing-${idx}`"
                label="Toon op pakbon"
                v-model="rule.showOnPackingSlip"
              />
            </div>
            <SelectInput
              v-if="rule.showOnQuote"
              :id="`quote-placement-${idx}`"
              label="Plek op de offerte"
              v-model="rule.quotePlacement"
              :options="quotePlacementOptions"
              class="quote-placement-select"
            />
          </div>

          <!-- Trigger Section -->
          <div class="rule-section">
            <h5>Wanneer toevoegen?</h5>
            <div class="trigger-section">
              <SelectInput
                :id="`trigger-type-${idx}`"
                label="Trigger"
                v-model="rule.trigger"
                :options="triggerOptions"
                class="trigger-select"
              />

              <!-- Conditions (only when trigger = conditional) -->
              <div v-if="rule.trigger === 'conditional'" class="conditions-wrapper">
                <div v-for="(condition, cidx) in rule.conditions" :key="cidx" class="condition-row">
                  <SelectInput
                    :id="`condition-option-${idx}-${cidx}`"
                    label="Optie"
                    v-model="condition.optionId"
                    :options="productOptionsList"
                    class="condition-option-select"
                  />

                  <SelectInput
                    :id="`condition-type-${idx}-${cidx}`"
                    label="Voorwaarde"
                    v-model="condition.type"
                    :options="conditionTypeOptions"
                    class="condition-type-select"
                  />

                  <!-- Value input based on condition type -->
                  <template v-if="condition.type === 'config_option_equals'">
                    <SelectInput
                      v-if="getOptionChoices(condition.optionId).length"
                      :id="`condition-value-${idx}-${cidx}`"
                      label="Waarde"
                      v-model="condition.choiceId"
                      :options="getOptionChoices(condition.optionId)"
                      class="condition-value-select"
                    />
                    <TextInput
                      v-else
                      :id="`condition-value-${idx}-${cidx}`"
                      label="Waarde"
                      v-model="condition.value"
                      placeholder="Waarde"
                      class="condition-value-input"
                    />
                  </template>

                  <NumberInput
                    v-else-if="condition.type === 'config_option_min'"
                    :id="`condition-min-${idx}-${cidx}`"
                    label="Minimum"
                    v-model="condition.minValue"
                    class="condition-min-input"
                  />

                  <NumberInput
                    v-else-if="condition.type === 'config_option_max'"
                    :id="`condition-max-${idx}-${cidx}`"
                    label="Maximum"
                    v-model="condition.maxValue"
                    class="condition-max-input"
                  />

                  <template v-else-if="condition.type === 'config_option_between'">
                    <NumberInput
                      :id="`condition-min-${idx}-${cidx}`"
                      label="Min"
                      v-model="condition.minValue"
                      class="condition-range-input"
                    />
                    <NumberInput
                      :id="`condition-max-${idx}-${cidx}`"
                      label="Max"
                      v-model="condition.maxValue"
                      class="condition-range-input"
                    />
                  </template>

                  <Btn
                    v-if="rule.conditions.length > 1"
                    icon="minus"
                    variant="plain"
                    size="sm"
                    @click="removeCondition(idx, cidx)"
                    title="Verwijder voorwaarde"
                  />
                </div>

                <Btn variant="secondary" size="sm" icon="plus" @click="addCondition(idx)">
                  Voorwaarde Toevoegen
                </Btn>
              </div>
            </div>
          </div>

          <!-- Preview Section -->
          <div class="rule-section rule-preview">
            <h5>Preview</h5>
            <div class="preview-content">
              <div class="preview-item">
                <strong>Item:</strong>
                {{ rule.name || 'Geen naam' }} ({{ rule.sku || 'Geen SKU' }})
                <span v-if="rule.sourceType === 'product' && rule.productId" class="preview-linked-badge">
                  📦 Gekoppeld product
                </span>
              </div>
              <div class="preview-price">
                <strong>Prijs:</strong>
                {{ rule.price ? '€' + rule.price.toFixed(2) : 'Gratis' }}
                {{ rule.quantity > 1 || rule.quantityType === 'calculated' ? ' per stuk' : '' }}
              </div>
              <div class="preview-quantity">
                <strong>Aantal:</strong>
                {{
                  rule.quantityType === 'fixed'
                    ? rule.quantity + 'x'
                    : 'Berekend: ' + (rule.quantityFormula || '-')
                }}
              </div>
              <div class="preview-trigger">
                <strong>Wanneer:</strong>
                {{ formatTriggerPreview(rule) }}
              </div>
              <div class="preview-visibility">
                <strong>Zichtbaar op:</strong>
                {{
                  [rule.showOnQuote ? 'Offerte' : null, rule.showOnPackingSlip ? 'Pakbon' : null]
                    .filter(Boolean)
                    .join(', ') || 'Nergens'
                }}{{
                  rule.showOnQuote
                    ? rule.quotePlacement === 'configuration'
                      ? ' (bij de configuratie)'
                      : ' (extra optie)'
                    : ''
                }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Btn variant="dashed" @click="addRule" icon="plus" class="add-rule-btn">
        Item toevoegen
      </Btn>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, nextTick } from 'vue';
  import { uid } from 'uid';

  // Input components
  import TextInput from '@/components/inputs/TextInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import CurrencyInput from '@/components/inputs/CurrencyInput.vue';
  import SearchableProductSelect from '@/components/inputs/SearchableProductSelect.vue';
  import FormulaEditor from '@/components/inputs/FormulaEditor.vue';

  // UI components
  import Btn from '@/components/partials/Btn.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';

  export default {
    name: 'AutoIncludeRulesEditor',

    components: {
      TextInput,
      NumberInput,
      SelectInput,
      ToggleInput,
      CurrencyInput,
      SearchableProductSelect,
      FormulaEditor,
      Btn,
      EmptyState,
    },

    props: {
      modelValue: {
        type: Array,
        default: () => [],
      },
      productOptions: {
        type: Array,
        default: () => [],
        description: 'Available product options for condition building',
      },
      computedFields: {
        type: Array,
        default: () => [],
        description: 'Computed fields that can be used in quantity formulas',
      },
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
      // Initialize with backward compatibility - add sourceType if missing
      const localRules = ref(
        props.modelValue.map(rule => ({
          ...rule,
          sourceType: rule.sourceType || (rule.productId ? 'product' : 'manual'),
          quotePlacement: rule.quotePlacement === 'configuration' ? 'configuration' : 'extra_option',
        }))
      );
      const isUpdatingFromExternal = ref(false);

      // Watch for external changes
      watch(
        () => props.modelValue,
        (newRules) => {
          // Only update if the value actually changed
          const newRulesStr = JSON.stringify(newRules);
          const currentRulesStr = JSON.stringify(localRules.value);
          if (newRulesStr !== currentRulesStr) {
            isUpdatingFromExternal.value = true;
            // Ensure backward compatibility - add sourceType if missing
            localRules.value = newRules.map(rule => ({
              ...rule,
              sourceType: rule.sourceType || (rule.productId ? 'product' : 'manual'),
              quotePlacement:
                rule.quotePlacement === 'configuration' ? 'configuration' : 'extra_option',
            }));
            // Reset flag after update cycle
            nextTick(() => {
              isUpdatingFromExternal.value = false;
            });
          }
        }
      );

      // Emit changes
      watch(
        localRules,
        (newRules) => {
          // Don't emit if we're updating from external source
          if (isUpdatingFromExternal.value) {
            return;
          }
          // Convert quantityType back to the expected format
          const formattedRules = newRules.map((rule) => ({
            ...rule,
            quantity: rule.quantityType === 'calculated' ? 'calculated' : rule.quantity,
          }));
          emit('update:modelValue', formattedRules);
        },
        { deep: true }
      );

      // Product options as select options
      const productOptionsList = computed(() => {
        return props.productOptions.map((option) => ({
          value: option.id,
          label: option.label,
        }));
      });

      // Formula fields for FormulaEditor - same format as CrossOptionRulesEditor
      const formulaFields = computed(() => {
        const fields = props.productOptions.map((option) => ({
          id: option.id,
          label: option.label,
          type: option.type === 'number' ? 'number' : 'single-choice',
          choices:
            option.type === 'single-choice' || option.type === 'multi-choice'
              ? option.choiceConfig?.choices
              : null,
        }));

        if (props.computedFields && Array.isArray(props.computedFields)) {
          props.computedFields.forEach((field) => {
            if (field.id && field.label) {
              fields.push({
                id: field.id,
                label: `📊 ${field.label}`,
                type: 'number',
              });
            }
          });
        }

        return fields;
      });

      // Get choices for a specific option
      const getOptionChoices = (optionId) => {
        const option = props.productOptions.find((o) => o.id === optionId);
        if (!option || !option.choiceConfig?.choices) return [];

        return option.choiceConfig.choices.map((choice) => ({
          value: choice.id,
          label: choice.label,
        }));
      };

      // Options
      const sourceTypeOptions = [
        { value: 'manual', label: 'Handmatig invoeren' },
        { value: 'product', label: 'Product uit catalogus' },
      ];

      const quantityTypeOptions = [
        { value: 'fixed', label: 'Vast aantal' },
        { value: 'calculated', label: 'Berekend (formule)' },
      ];

      const quotePlacementOptions = [
        { value: 'extra_option', label: 'Extra optie' },
        { value: 'configuration', label: 'Onderdeel van de configuratie' },
      ];

      const triggerOptions = [
        { value: 'always', label: 'Altijd toevoegen' },
        { value: 'conditional', label: 'Alleen bij bepaalde voorwaarden' },
      ];

      const conditionTypeOptions = [
        { value: 'config_option_equals', label: 'Optie is gelijk aan' },
        { value: 'config_option_min', label: 'Optie is minimaal' },
        { value: 'config_option_max', label: 'Optie is maximaal' },
        { value: 'config_option_between', label: 'Optie is tussen' },
      ];

      // Handle product selection from SearchableProductSelect
      const handleProductSelected = (ruleIndex, product) => {
        if (!product) return;
        
        const rule = localRules.value[ruleIndex];
        rule.productId = product.id;
        rule.name = product.title || product.name;
        rule.sku = product.sku || product.base_sku || product.baseSku || '';
        rule.price = product.price || 0;
        
        // Also store the product reference for later use
        rule.linkedProduct = {
          id: product.id,
          title: product.title || product.name,
          sku: rule.sku,
          price: rule.price,
          imageUrl: product.imageUrl,
        };
      };

      // Currency formatter
      const formatCurrency = (amount) => {
        return new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
        }).format(amount || 0);
      };

      // Helper functions
      const formatTriggerPreview = (rule) => {
        if (rule.trigger === 'always') return 'Altijd';

        if (!rule.conditions?.length) return 'Geen voorwaarden ingesteld';

        return rule.conditions
          .map((c) => {
            const option = props.productOptions.find((o) => o.id === c.optionId);
            const optionLabel = option?.label || c.optionId;

            switch (c.type) {
              case 'config_option_equals': {
                const choice = option?.choiceConfig?.choices?.find((ch) => ch.id === c.choiceId);
                const valueLabel = choice?.label || c.value || c.choiceId;
                return `${optionLabel} = ${valueLabel}`;
              }
              case 'config_option_min':
                return `${optionLabel} ≥ ${c.minValue}`;
              case 'config_option_max':
                return `${optionLabel} ≤ ${c.maxValue}`;
              case 'config_option_between':
                return `${optionLabel} tussen ${c.minValue} en ${c.maxValue}`;
              default:
                return c.type;
            }
          })
          .join(' EN ');
      };

      // Rule management
      const addRule = () => {
        const newRule = {
          id: `auto_${uid()}`,
          sourceType: 'manual', // 'manual' or 'product'
          productId: null, // Linked product ID (when sourceType = 'product')
          linkedProduct: null, // Product reference data
          name: '',
          description: '',
          sku: '',
          price: 0,
          quantity: 1,
          quantityType: 'fixed',
          quantityFormula: '',
          showOnQuote: true,
          showOnPackingSlip: true,
          quotePlacement: 'extra_option',
          trigger: 'always',
          conditions: [],
        };

        localRules.value.push(newRule);
      };

      const removeRule = (index) => {
        localRules.value.splice(index, 1);
      };

      const addCondition = (ruleIndex) => {
        if (!localRules.value[ruleIndex].conditions) {
          localRules.value[ruleIndex].conditions = [];
        }

        localRules.value[ruleIndex].conditions.push({
          type: 'config_option_equals',
          optionId: props.productOptions[0]?.id || '',
          choiceId: '',
          value: '',
          minValue: 0,
          maxValue: 100,
        });
      };

      const removeCondition = (ruleIndex, conditionIndex) => {
        localRules.value[ruleIndex].conditions.splice(conditionIndex, 1);
      };

      return {
        localRules,
        productOptionsList,
        getOptionChoices,
        sourceTypeOptions,
        quantityTypeOptions,
        quotePlacementOptions,
        triggerOptions,
        conditionTypeOptions,
        formatTriggerPreview,
        formulaFields,
        handleProductSelected,
        formatCurrency,
        addRule,
        removeRule,
        addCondition,
        removeCondition,
      };
    },
  };
</script>

<style scoped lang="scss">
  .auto-include-rules-editor {
    .rules-list {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
    }

    .rule-card {
      @include rule-card;

      .rule-header {
        @include rule-card-header;

        .rule-info {
          flex: 1;
          display: grid;
          grid-template-columns: 140px 1fr 1fr 150px;
          gap: 1rem;
          align-items: end;

          .rule-source-select {
            min-width: 140px;
          }

          .rule-product-select {
            grid-column: span 2;
          }

          .product-info-display {
            display: flex;
            gap: 0.5rem;
            align-items: center;
            padding-bottom: 0.5rem;

            .product-sku-badge {
              font-size: 0.75rem;
              padding: 0.25rem 0.5rem;
              background: var(--slate-100);
              color: var(--slate-600);
              border-radius: 0.25rem;
              font-family: 'JetBrains Mono', monospace;
            }

            .product-price-badge {
              font-size: 0.75rem;
              padding: 0.25rem 0.5rem;
              background: var(--green-100);
              color: var(--green-700);
              border-radius: 0.25rem;
              font-weight: 600;
            }
          }
        }
      }

      .rule-body {
        @include rule-card-body;
      }

      .rule-section {
        @include rule-card-section;

        h5 {
          @include rule-card-section-title;
        }
      }

      .quantity-row {
        display: grid;
        grid-template-columns: 200px 1fr;
        gap: 1rem;
        align-items: start;
      }

      .formula-input-group {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        .formula-help {
          font-size: 0.75rem;
          color: var(--slate-500);
          margin: 0;

          code {
            background: var(--slate-100);
            padding: 0.125rem 0.375rem;
            border-radius: 0.25rem;
            font-family: 'JetBrains Mono', monospace;
            font-size: 0.7rem;
          }
        }
      }

      .visibility-row {
        display: flex;
        gap: 2rem;
      }

      .trigger-section {
        .trigger-select {
          max-width: 300px;
          margin-bottom: 1rem;
        }
      }

      .conditions-wrapper {
        padding: 1rem;
        background: var(--blue-50);
        border-radius: 6px;
        border: 1px solid var(--blue-200);

        .condition-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr auto;
          gap: 1rem;
          align-items: end;
          margin-bottom: 0.75rem;

          &:last-of-type {
            margin-bottom: 1rem;
          }
        }

        .condition-range-input {
          max-width: 100px;
        }
      }

      .rule-preview {
        @include rule-card-preview;

        .preview-content {
          .preview-item,
          .preview-price,
          .preview-quantity,
          .preview-trigger,
          .preview-visibility {
            margin-bottom: 0.5rem;

            &:last-child {
              margin-bottom: 0;
            }
          }

          .preview-linked-badge {
            display: inline-block;
            margin-left: 0.5rem;
            font-size: 0.7rem;
            padding: 0.125rem 0.375rem;
            background: var(--blue-100);
            color: var(--blue-700);
            border-radius: 0.25rem;
          }
        }
      }
    }

    .add-rule-btn {
      margin-top: 0.5rem;
    }
  }
</style>
